import React, { useState, useRef, useEffect, useContext } from "react";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";

const API_CHAT = process.env.REACT_APP_API_ASISTENTE_IA;
const API_BIENVENIDA = "/api/AsistenteIA/bienvenida";
const API_EXCEL = "/api/AsistenteIA/descargar-cupones-excel";

const VERDE_ZOCO = "#b4c400";
const VERDE_OSCURO = "#96a300";
const DARK_BG = "#292b2f";

export default function ChatWidgetIA() {
  const { darkMode } = useContext(DarkModeContext);
  const [abierto, setAbierto] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [saludado, setSaludado] = useState(false);
  const mensajesRef = useRef(null);

  const bg = darkMode ? DARK_BG : "#ffffff";
  const assistantBubble = darkMode ? "#3a3d43" : "#f0f0f0";
  const textColor = darkMode ? "#f5f5f5" : "#1a1a1a";
  const borderColor = darkMode ? "#3d3d5c" : "#e0e0e0";
  const inputBg = darkMode ? "#3a3d43" : "#f5f5f5";

  useEffect(() => {
    if (mensajesRef.current)
      mensajesRef.current.scrollTop = mensajesRef.current.scrollHeight;
  }, [historial, cargando]);

  useEffect(() => {
    if (abierto && !saludado) {
      saludar();
      setSaludado(true);
    }
  }, [abierto]);

  const saludar = async () => {
    const token = getToken();
    if (!token) return;
    setCargando(true);
    try {
      const res = await fetch(API_BIENVENIDA, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setHistorial([{ rol: "assistant", contenido: data.respuesta }]);
    } catch {
      setHistorial([{ rol: "assistant", contenido: "¡Hola! Soy Don Zoco, tu asistente. ¿En qué te puedo ayudar?" }]);
    } finally {
      setCargando(false);
    }
  };

  const descargarExcel = async (filtros) => {
    const token = getToken();
    try {
      const res = await fetch(API_EXCEL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          nroCupon: filtros.NroCupon,
          fecha: filtros.Fecha,
          tarjeta: filtros.Tarjeta,
          ultimos4: filtros.Ultimos4,
          monto: filtros.Monto,
          cuotas: filtros.Cuotas,
        }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cupones_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const enviarConTexto = async (texto) => {
    if (!texto || cargando) return;
    const token = getToken();
    if (!token) return;

    const historialAnterior = historial;
    const nuevoHistorial = [...historial, { rol: "user", contenido: texto }];
    setHistorial(nuevoHistorial);
    setCargando(true);

    try {
      const res = await fetch(API_CHAT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          mensaje: texto,
          historial: historialAnterior.map((h) => ({ Rol: h.rol, Contenido: h.contenido })),
        }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);

      setHistorial([...nuevoHistorial, { rol: "assistant", contenido: "" }]);
      setCargando(false);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let textoAcumulado = "";
      let excelPendiente = null;

      outer: while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;
          try {
            const ev = JSON.parse(jsonStr);
            if (ev.type === "chunk") {
              textoAcumulado += ev.text;
              setHistorial((h) => {
                const upd = [...h];
                upd[upd.length - 1] = { ...upd[upd.length - 1], contenido: textoAcumulado };
                return upd;
              });
            } else if (ev.type === "excel") {
              excelPendiente = ev;
            } else if (ev.type === "done") {
              if (excelPendiente) {
                setHistorial((h) => {
                  const upd = [...h];
                  upd[upd.length - 1] = {
                    ...upd[upd.length - 1],
                    excelFiltros: excelPendiente.filtros,
                    excelLabel: excelPendiente.label,
                  };
                  return upd;
                });
              }
              break outer;
            }
          } catch {}
        }
      }
    } catch {
      setHistorial((h) => {
        const upd = [...h];
        const ultimo = upd[upd.length - 1];
        if (ultimo?.rol === "assistant" && !ultimo.contenido) {
          upd[upd.length - 1] = { ...ultimo, contenido: "Ocurrió un error. Intentá de nuevo." };
          return upd;
        }
        return [...upd, { rol: "assistant", contenido: "Ocurrió un error. Intentá de nuevo." }];
      });
    } finally {
      setCargando(false);
    }
  };

  const enviar = () => {
    const texto = mensaje.trim();
    if (!texto) return;
    setMensaje("");
    enviarConTexto(texto);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999 }}>
      {abierto && (
        <div style={{
          width: 360,
          height: 510,
          background: bg,
          borderRadius: 16,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          marginBottom: 12,
          border: `1px solid ${borderColor}`,
        }}>
          {/* Header */}
          <div style={{
            background: VERDE_ZOCO,
            padding: "13px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "rgba(0,0,0,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
              }}>
                🤖
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: DARK_BG }}>Don Zoco</div>
                <div style={{ fontSize: 11, color: DARK_BG, opacity: 0.7 }}>Asistente de Zoco</div>
              </div>
            </div>
            <button
              onClick={() => setAbierto(false)}
              style={{
                background: "none", border: "none",
                color: DARK_BG, fontSize: 22, cursor: "pointer",
                lineHeight: 1, padding: 0, opacity: 0.7,
              }}
            >×</button>
          </div>

          {/* Mensajes */}
          <div ref={mensajesRef} style={{
            flex: 1, overflowY: "auto",
            padding: "14px 14px 8px",
            display: "flex", flexDirection: "column", gap: 10,
          }}>
            {historial.length === 0 && !cargando && (
              <div style={{ textAlign: "center", color: darkMode ? "#888" : "#aaa", fontSize: 13, marginTop: 40 }}>
                Abriendo Don Zoco...
              </div>
            )}
            {historial.length === 1 && historial[0].rol === "assistant" && !cargando && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 2 }}>
                {[
                  "💰 ¿Cuánto facturé este mes?",
                  "📊 Desglose por tarjeta",
                  "🔍 Buscar un cupón",
                  "📅 ¿Qué meses tengo disponibles?",
                ].map((chip) => (
                  <button
                    key={chip}
                    onClick={() => enviarConTexto(chip)}
                    style={{
                      background: "none",
                      border: `1px solid ${VERDE_ZOCO}`,
                      borderRadius: 20,
                      padding: "5px 11px",
                      fontSize: 12,
                      color: darkMode ? VERDE_ZOCO : VERDE_OSCURO,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = darkMode ? "rgba(180,196,0,0.12)" : "rgba(180,196,0,0.1)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            )}
            {historial.map((h, i) => (
              <div key={i} style={{ display: "flex", justifyContent: h.rol === "user" ? "flex-end" : "flex-start" }}>
                <div style={{ maxWidth: "82%", display: "flex", flexDirection: "column", gap: 6,
                              alignItems: h.rol === "user" ? "flex-end" : "flex-start" }}>
                  <div style={{
                    padding: "9px 13px",
                    borderRadius: h.rol === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    background: h.rol === "user" ? VERDE_ZOCO : assistantBubble,
                    color: h.rol === "user" ? DARK_BG : textColor,
                    fontSize: 13,
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontWeight: h.rol === "user" ? 500 : 400,
                  }}>
                    {renderTexto(h.contenido)}
                  </div>
                  {h.excelFiltros && (
                    <button
                      onClick={() => descargarExcel(h.excelFiltros)}
                      style={{
                        background: VERDE_ZOCO, border: "none", borderRadius: 8,
                        padding: "6px 12px", cursor: "pointer",
                        fontSize: 12, fontWeight: 600, color: DARK_BG,
                      }}
                    >
                      {h.excelLabel || "📥 Descargar Excel"}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {cargando && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div style={{
                  padding: "9px 14px",
                  borderRadius: "16px 16px 16px 4px",
                  background: assistantBubble,
                  fontSize: 13,
                }}>
                  <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                    <Dot delay={0} /><Dot delay={200} /><Dot delay={400} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{
            padding: "10px 12px",
            borderTop: `1px solid ${borderColor}`,
            display: "flex", gap: 8,
            flexShrink: 0, background: bg,
          }}>
            <textarea
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribí tu consulta..."
              rows={1}
              style={{
                flex: 1, resize: "none",
                border: `1px solid ${borderColor}`,
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 13,
                background: inputBg,
                color: textColor,
                outline: "none",
                fontFamily: "inherit",
                lineHeight: 1.4,
              }}
            />
            <button
              onClick={enviar}
              disabled={cargando || !mensaje.trim()}
              style={{
                background: cargando || !mensaje.trim() ? "#ccc" : VERDE_ZOCO,
                border: "none", borderRadius: 10,
                width: 38, height: 38,
                cursor: cargando || !mensaje.trim() ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, alignSelf: "flex-end",
                transition: "background 0.2s",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={DARK_BG}>
                <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Botón flotante */}
      <button
        onClick={() => setAbierto(!abierto)}
        style={{
          width: 56, height: 56,
          borderRadius: "50%",
          background: VERDE_ZOCO,
          border: "none", cursor: "pointer",
          boxShadow: "0 4px 16px rgba(180,196,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: abierto ? 24 : 26,
          transition: "transform 0.2s",
          marginLeft: "auto",
          color: DARK_BG,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        title="Don Zoco - Asistente"
      >
        {abierto ? "×" : "🤖"}
      </button>
    </div>
  );
}

function renderTexto(texto) {
  if (!texto || !texto.includes("**")) return texto;
  const partes = texto.split("**");
  return partes.map((parte, i) =>
    i % 2 === 1 ? <strong key={i}>{parte}</strong> : parte
  );
}

function Dot({ delay }) {
  return (
    <span style={{
      width: 7, height: 7, borderRadius: "50%",
      background: "#b4c400",
      display: "inline-block",
      animation: `donzoco-bounce 1s ${delay}ms infinite`,
    }} />
  );
}
